import React, { useState, useReducer, } from 'react';
import { PAYMENTS_PAGE_SIZE } from '../constants/general';
import { DEFAULT_ROWS_PER_PAGE } from '../constants/payments-table';
import { CardType } from '../constants/payments';
import { B2BCreditCardsFilterType, b2bPaymentsApi, } from '../api/b2b/payments';
import { b2cPaymentsApi } from '../api/b2c/payments';
import { mapB2BPaymentCardsToCommonModel, mapB2CPaymentCardsToCommonModel, } from '../utils/mapping-payments';
import { determineT2CPrimaryForPHA, isT2CPrimaryPresent, } from '../utils/calculate-t2c-primary';
import { TAlertStatus, } from '../models/common/alert-messages';
import { useIsMounted } from '../hooks/use-is-mounted';
import { cardSortByPayloadValueMap } from '../constants/sort';
import { creditCardFormReducer, } from './reducers';
import { setPageInfo } from '../utils/analytics';
import { ERR_GENERIC } from '../constants/response-error-messages';
import { getPaymentsErrorCode } from '../utils/payments';
import { useLocalStorage } from '../hooks/use-local-storage';
import { INFO_MESSAGE_AUTHORIZE_CARD } from '../constants/response-success-messages';
import { Alert } from '../components/shared/alert/alert';
import { useSearchParam } from '../hooks/use-search-param';
import { cardTypePageSectionMapping } from '../constants/analytics';
import { b2bAccountApi } from '../api/b2b/account';
import { removeNonDigitsFromString, getMAApproverList } from '../utils/general';
const determineFilter = (filterConfig) => {
    const filterType = (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.filterType) || B2BCreditCardsFilterType.NOTHING;
    let filterValue = (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.filterValue) || '';
    if (filterValue &&
        (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.filterType) === B2BCreditCardsFilterType.expDate) {
        filterValue = filterValue.replace('/', '20');
    }
    return { filterType, filterValue };
};
const PaymentsContext = React.createContext(null);
const PaymentsProvider = ({ children, isB2B }) => {
    const [loading, setLoading] = useState(false);
    const [creditCards, setCreditCards] = useState(null);
    const [paypalCards, setPaypalCards] = useState(null);
    const [proXtraDollarCards, setProXtraDollarCards] = useState(null);
    const [giftCards, setGiftCards] = useState(null);
    const [checkingAccounts, setCheckingAccounts] = useState(null);
    const [keytags, setKeytags] = useState(null);
    const [linesOfCredit, setLinesOfCredit] = useState(null);
    const [profileDetails, setProfileDetails] = useState(null);
    const [paginationInfo, setPaginationInfo] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const [t2cCreditCards, setT2cCreditCards] = useState(null);
    const [t2cMultipleApproverContacts, sett2cMultipleApproverContacts] = useState(null);
    const [isListView, setIsListView] = useLocalStorage('isListView', true);
    const defaultPageSize = isListView
        ? DEFAULT_ROWS_PER_PAGE
        : PAYMENTS_PAGE_SIZE;
    const [currentSortByValue, setCurrentSortByValue] = useState({
        sortBy: null,
        ascendingOrder: null,
        pageSize: defaultPageSize,
    });
    const [currentCardType, setCurrentCardType] = useState(null);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [submittedSearchValue, setSubmittedSearchValue] = useState('');
    const [submittedSearchType, setSubmittedSearchType] = useState(B2BCreditCardsFilterType.NOTHING);
    const [creditCardForm, dispatchCreditCardForm] = useReducer(creditCardFormReducer, {
        mode: null,
        card: null,
        origin: '',
    });
    const isMountedRef = useIsMounted();
    const [defaultCheckoutPayment, setDefaultCheckoutPayment] = useState(null);
    const fromProAllowance = useSearchParam('fromProAllowance') === 'true';
    const [proAllowanceMessage, setProAllowanceMessage] = useState(() => {
        return fromProAllowance
            ? {
                hideHDCC: true,
                content: (React.createElement(Alert, { status: "info", "data-testid": "AuthCardInfoMsg" }, INFO_MESSAGE_AUTHORIZE_CARD.message)),
            }
            : null;
    });
    const setCardsData = (cardType, data) => {
        setCurrentCardType(cardType);
        switch (cardType) {
            case CardType.CREDIT_CARD:
                setCreditCards(data);
                break;
            case CardType.PAYPAL_ACCOUNT:
                setPaypalCards(data);
                break;
            case CardType.PRO_XTRA_DOLLARS:
                setProXtraDollarCards(data);
                break;
            case CardType.GIFT_CARD:
                setGiftCards(data);
                break;
            case CardType.CHECKING_ACCOUNT:
                setCheckingAccounts(data);
                break;
            case CardType.KEY_TAG:
                setKeytags(data);
                break;
            case CardType.PRO_HOUSE_ACCOUNT:
                setLinesOfCredit(data);
                break;
            default:
        }
    };
    const getAccountProfile = (forceFetch = false) => {
        if (!forceFetch && profileDetails) {
            return Promise.resolve(profileDetails);
        }
        return b2bAccountApi.profileDetails().then(({ data }) => {
            if (isMountedRef.current) {
                setProfileDetails(data.accountProfile);
            }
            return data.accountProfile;
        });
    };
    const determineSortBy = (sortConfig) => {
        return {
            page: (sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.page) || 1,
            sortBy: (sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.sortBy) || 'lastedited',
            ascendingOrder: (sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.ascendingOrder) || false,
            pageSize: (sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.pageSize) || currentSortByValue.pageSize || defaultPageSize,
        };
    };
    const handleError = (paymentId, message, dataTestId) => {
        setAlertMessage({
            paymentId,
            alertStatus: TAlertStatus.ERROR,
            text: message,
            dataTestId,
        });
    };
    const catchError = (error, cardType) => {
        const errorCode = getPaymentsErrorCode(error);
        handleError('', ERR_GENERIC.message);
        setPageInfo('error', 'page', cardTypePageSectionMapping[cardType], ERR_GENERIC.message, errorCode || 'n/a');
        throw error;
    };
    const getB2BPayments = (cardType, sortConfig, filterConfig, shouldFetchPHAForCC) => {
        if (cardType === CardType.PRO_HOUSE_ACCOUNT) {
            const getT2cCreditCards = b2bPaymentsApi.getPayments(CardType.CREDIT_CARD, {
                page: 1,
                sortBy: 'text2confirm',
                ascendingOrder: true,
                pageSize: defaultPageSize,
            }, {
                filterType: B2BCreditCardsFilterType.NOTHING,
                filterValue: '',
            });
            setLoading(true);
            return Promise.all([
                b2bPaymentsApi.getPHACards(),
                getAccountProfile(),
                getT2cCreditCards,
            ])
                .then(([{ data }, { customerProfile: { businessName }, }, { data: ccData },]) => {
                const t2cApprovers = getMAApproverList(ccData.paymentCards.t2cApprovers, data.paymentCards.t2cApprovers);
                const { paymentCards, paginationInfo: ccPaginationInfo } = mapB2BPaymentCardsToCommonModel({
                    paymentCards: ccData.paymentCards.paymentCard,
                    paginationInfo: ccData.paginationInfo,
                    t2cApprovers,
                });
                setT2cCreditCards(paymentCards.paymentCards);
                return mapB2BPaymentCardsToCommonModel({
                    paymentCards: data.paymentCards.paymentCard,
                    paginationInfo: data.paginationInfo,
                    filterConfig: undefined,
                    oldPaginationInfo: undefined,
                    businessName,
                    t2cNumber: ccPaginationInfo === null || ccPaginationInfo === void 0 ? void 0 : ccPaginationInfo.t2cNumber,
                    t2cApprovers,
                });
            })
                .finally(() => {
                if (isMountedRef.current)
                    setLoading(false);
            });
        }
        if (cardType === CardType.CREDIT_CARD) {
            const defaultPHARes = {
                data: {
                    paginationInfo: { t2cNumber: null },
                    paymentCards: { t2cApprovers: [] },
                },
            };
            setLoading(true);
            return Promise.all([
                b2bPaymentsApi.getPayments(cardType, Object.assign({}, sortConfig), Object.assign({}, filterConfig)),
                shouldFetchPHAForCC
                    ? b2bPaymentsApi.getPHACards().catch(() => {
                        // if this call fails or is blocked, we don't want to show a page level error for credit cards
                        return defaultPHARes;
                    })
                    : Promise.resolve(defaultPHARes),
            ])
                .then(([{ data: paymentsData }, { data: phaData }]) => {
                var _a;
                const t2cApprovers = getMAApproverList(paymentsData.paymentCards.t2cApprovers, phaData.paymentCards.t2cApprovers);
                return mapB2BPaymentCardsToCommonModel({
                    paymentCards: paymentsData.paymentCards.paymentCard,
                    paginationInfo: paymentsData.paginationInfo,
                    filterConfig,
                    oldPaginationInfo: paginationInfo,
                    businessName: undefined,
                    t2cNumber: ((_a = phaData === null || phaData === void 0 ? void 0 : phaData.paginationInfo) === null || _a === void 0 ? void 0 : _a.t2cNumber) || '',
                    t2cApprovers,
                });
            })
                .finally(() => {
                if (isMountedRef.current)
                    setLoading(false);
            });
        }
        setLoading(true);
        return b2bPaymentsApi
            .getPayments(cardType, Object.assign({}, sortConfig), Object.assign({}, filterConfig))
            .then(({ data }) => {
            return mapB2BPaymentCardsToCommonModel({
                paymentCards: data.paymentCards.paymentCard,
                paginationInfo: data.paginationInfo,
                filterConfig,
                oldPaginationInfo: paginationInfo,
                t2cApprovers: data.paymentCards.t2cApprovers || null,
            });
        })
            .finally(() => {
            if (isMountedRef.current)
                setLoading(false);
        });
    };
    const getPaymentCards = (cardType, fetchOnly = false, shouldFetchPHAForCC = false, sortConfig, filterConfig) => {
        if (currentCardType !== cardType && !fetchOnly) {
            setPaginationInfo((prevState) => prevState
                ? Object.assign(Object.assign({}, prevState), { totalPages: 0, totalCreditCards: 0, totalPayPalAccounts: 0, totalGiftCards: 0, totalProXtraDollars: 0, totalCheckingAccounts: 0, totalKeyTags: 0, totalLinesOfCredit: 0 }) : prevState);
        }
        if (!isB2B &&
            (cardType === CardType.CREDIT_CARD ||
                cardType === CardType.PAYPAL_ACCOUNT)) {
            setLoading(true);
            return b2cPaymentsApi
                .getCreditAndPaypalCards()
                .then(({ data }) => {
                const { paymentCards, paginationInfo: mappedPaginationInfo } = mapB2CPaymentCardsToCommonModel(cardType, data.paymentCards.paymentCard, data.paginationInfo);
                const instantDefaultPayment = data === null || data === void 0 ? void 0 : data.paymentCards.paymentCard.find((payment) => payment.instantCheckoutDefault);
                paymentCards === null || paymentCards === void 0 ? void 0 : paymentCards.paymentCards.sort((card1, card2) => {
                    if (card1.isDefault)
                        return -1;
                    if (card2.isDefault ||
                        Number(card2.lastModifiedDate) > Number(card1.lastModifiedDate))
                        return 1;
                    if (Number(card2.lastModifiedDate) < Number(card1.lastModifiedDate))
                        return -1;
                    return 0;
                });
                if (isMountedRef.current && !fetchOnly) {
                    setDefaultCheckoutPayment(instantDefaultPayment || null);
                    setCardsData(cardType, paymentCards || null);
                    setPaginationInfo(mappedPaginationInfo || null);
                    setCurrentPageNumber((sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.page) || 1);
                }
                return paymentCards === null || paymentCards === void 0 ? void 0 : paymentCards.paymentCards;
            })
                .catch((error) => catchError(error, cardType))
                .finally(() => {
                if (isMountedRef.current)
                    setLoading(false);
            });
        }
        const { sortBy, ascendingOrder, pageSize, page } = determineSortBy(sortConfig);
        setCurrentSortByValue({
            sortBy: cardSortByPayloadValueMap[sortBy],
            ascendingOrder,
            pageSize,
        });
        const { filterType, filterValue } = determineFilter(filterConfig);
        return getB2BPayments(cardType, { sortBy, ascendingOrder, pageSize, page }, { filterType, filterValue }, shouldFetchPHAForCC)
            .then(({ paymentCards, paginationInfo: mappedPaginationInfo, t2cApprovers, }) => {
            if (isMountedRef.current && !fetchOnly) {
                sett2cMultipleApproverContacts(t2cApprovers || null);
                setSubmittedSearchType(filterType);
                setSubmittedSearchValue(filterValue);
                setIsSearchActive(!!filterType);
                setPaginationInfo(mappedPaginationInfo || null);
                setCurrentPageNumber((mappedPaginationInfo === null || mappedPaginationInfo === void 0 ? void 0 : mappedPaginationInfo.pageNumber) || 1);
                setCardsData(cardType, paymentCards || null);
            }
            return paymentCards === null || paymentCards === void 0 ? void 0 : paymentCards.paymentCards;
        })
            .catch((error) => catchError(error, cardType));
    };
    const getPaymentById = (paymentId, typeCd) => {
        setLoading(true);
        return b2bPaymentsApi
            .getPaymentById(paymentId, typeCd)
            .then(({ data }) => {
            return data;
        })
            .catch((error) => catchError(error, typeCd))
            .finally(() => {
            if (isMountedRef.current) {
                setLoading(false);
            }
        });
    };
    const unenrollPHAT2C = ({ paymentId, t2cApprovers, }) => {
        const hasT2CApprovers = !!t2cApprovers && t2cApprovers.length > 0;
        setLoading(true);
        return b2bPaymentsApi
            .phaUnenrollT2C(hasT2CApprovers
            ? {
                paymentId,
                t2cApprovers,
            }
            : {
                paymentId,
            })
            .then(({ data }) => {
            return data;
        })
            .finally(() => {
            if (isMountedRef.current) {
                setLoading(false);
            }
        });
    };
    const enrollPHAT2C = ({ t2cNumber, paymentId, locType, isResend, t2cApprovers, }) => {
        setLoading(true);
        const getPHA = getPaymentCards(CardType.PRO_HOUSE_ACCOUNT);
        const getCC = getB2BPayments(CardType.CREDIT_CARD, {
            page: 1,
            sortBy: 'text2confirm',
            ascendingOrder: true,
            pageSize: defaultPageSize,
        }, {
            filterType: B2BCreditCardsFilterType.NOTHING,
            filterValue: '',
        }, false);
        return Promise.all([getPHA, getCC])
            .then(([paymentLOCCards, paymentCreditCards]) => {
            const restPaymentLOCCards = paymentLOCCards.filter((loc) => loc.paymentId !== paymentId);
            const t2cPrimary = determineT2CPrimaryForPHA(locType, isT2CPrimaryPresent(restPaymentLOCCards, paymentCreditCards.paymentCards.paymentCards));
            return t2cPrimary;
        })
            .then((t2cPrimary) => {
            const hasT2CApprovers = !!t2cApprovers && t2cApprovers.length > 0;
            return b2bPaymentsApi.phaEnrollT2C(hasT2CApprovers
                ? {
                    paymentId,
                    t2cPrimary,
                    t2cApprovers: t2cApprovers.map((approver) => {
                        return {
                            fullName: approver.fullName,
                            t2cNumber: removeNonDigitsFromString(approver.t2cNumber),
                        };
                    }),
                }
                : {
                    t2cNumber,
                    paymentId,
                    t2cPrimary,
                    isResend,
                });
        })
            .then(({ data }) => {
            return data;
        })
            .catch((error) => catchError(error, CardType.PRO_HOUSE_ACCOUNT))
            .finally(() => {
            if (isMountedRef.current) {
                setLoading(false);
            }
        });
    };
    return (React.createElement(PaymentsContext.Provider, { value: {
            isB2B,
            creditCards,
            paypalCards,
            proXtraDollarCards,
            giftCards,
            checkingAccounts,
            keytags,
            linesOfCredit,
            paginationInfo,
            creditCardForm,
            dispatchCreditCardForm,
            alertMessage,
            currentPageNumber,
            currentSortByValue,
            getPaymentCards,
            loading,
            setAlertMessage,
            isSearchActive,
            setCurrentSortByValue,
            submittedSearchValue,
            submittedSearchType,
            isListView,
            setIsListView,
            defaultCheckoutPayment,
            setDefaultCheckoutPayment,
            getPaymentById,
            proAllowanceMessage,
            setProAllowanceMessage,
            enrollPHAT2C,
            unenrollPHAT2C,
            getAccountProfile,
            t2cCreditCards,
            t2cMultipleApproverContacts,
        } }, children));
};
export { PaymentsProvider, PaymentsContext };
